import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Heading,
  Input,
  Wrap,
  Separator,
} from '@chakra-ui/react';
import { Tags, Save, Search, X } from 'lucide-react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { useGetConditionGroupConditionsQuery, useAssignConditionsToGroupMutation, useGetConditionGroupLookupQuery } from '../api';
import { useGetConditionLookupsQuery, ConditionLookup } from '@entities/conditions/api';
import ErrorAlert from '@/components/ErrorAlert';
import LoadingSkeleton from '@/components/LoadingSkeleton';

interface ConditionGroupConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number; // ID of condition group to fetch (for hash modal)
  onSuccess?: () => void;
}

const ConditionGroupConditionsModal: React.FC<ConditionGroupConditionsModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  // Fetch individual condition group
  const { 
    data: conditionGroup, 
    isLoading: isLoadingGroup, 
    error: groupError 
  } = useGetConditionGroupLookupQuery(entityId!, {
    skip: !entityId
  });
  const [selectedConditionIds, setSelectedConditionIds] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const { 
    data: conditionsResponse, 
    isLoading: isLoadingConditions,
    isError: isConditionsError,
    error: conditionsError 
  } = useGetConditionLookupsQuery({ per_page: 1000 }); // Fetch all conditions
  
  const allConditions = conditionsResponse?.data || [];
  
  const { 
    data: currentConditions = [], 
    isLoading: isLoadingCurrentConditions,
    isError: isCurrentConditionsError,
    error: currentConditionsError
  } = useGetConditionGroupConditionsQuery(entityId || 0, {
    skip: !entityId
  });
  
  const [
    assignConditions,
    { isLoading: isSaving, error: saveError }
  ] = useAssignConditionsToGroupMutation();

  // Initialize selected conditions when modal opens and data is loaded
  useEffect(() => {
    if (conditionGroup && currentConditions && !isLoadingCurrentConditions) {
      const currentConditionIds = currentConditions.map(condition => condition.id);
      setSelectedConditionIds(currentConditionIds);
    } else if (!isLoadingCurrentConditions) {
      setSelectedConditionIds([]);
    }
  }, [conditionGroup?.id, isLoadingCurrentConditions]); // Only depend on stable values

  const handleSave = async () => {
    if (!conditionGroup) return;

    try {
      await assignConditions({
        id: conditionGroup.id,
        data: { condition_ids: selectedConditionIds }
      }).unwrap();
      
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Failed to assign conditions:', error);
    }
  };

  // Filter conditions based on search term
  const filteredConditions = useMemo(() => {
    if (!searchTerm) return allConditions;
    return allConditions.filter((condition: ConditionLookup) => 
      condition.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      condition.speciality.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allConditions, searchTerm]);

  // Get selected conditions for display
  const selectedConditions = useMemo(() => {
    return allConditions.filter(condition => selectedConditionIds.includes(condition.id));
  }, [allConditions, selectedConditionIds]);

  // Get available (unselected) conditions for selection
  const availableConditions = useMemo(() => {
    return filteredConditions.filter(condition => !selectedConditionIds.includes(condition.id));
  }, [filteredConditions, selectedConditionIds]);

  const hasChanges = useMemo(() => {
    if (!currentConditions) return selectedConditionIds.length > 0;
    
    const currentConditionIds = currentConditions.map(condition => condition.id).sort();
    const newConditionIds = [...selectedConditionIds].sort();
    
    return JSON.stringify(currentConditionIds) !== JSON.stringify(newConditionIds);
  }, [currentConditions, selectedConditionIds]);

  const canSave = hasChanges;

  const handleRemoveCondition = (conditionId: number) => {
    setSelectedConditionIds(prev => prev.filter(id => id !== conditionId));
  };

  const handleAddCondition = (conditionId: number) => {
    setSelectedConditionIds(prev => [...prev, conditionId]);
  };

  const isLoading = isLoadingGroup || isLoadingConditions || isLoadingCurrentConditions;

  if (groupError) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} size="lg">
        <DrawerHeader>
          <DrawerTitle>Error Loading Condition Group</DrawerTitle>
          <DrawerCloseTrigger />
        </DrawerHeader>
        <DrawerBody>
          <Text color="red.500" textAlign="center">
            Error loading condition group details. Please try again.
          </Text>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DrawerFooter>
      </Drawer>
    );
  }

  if (!conditionGroup) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg">
      <DrawerHeader>
        <DrawerTitle>
          <HStack>
            <Tags size={20} />
            <Text>Manage Group Conditions</Text>
          </HStack>
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>

      <DrawerBody>
        <VStack gap={4} align="stretch">
          {/* Condition Group Info */}
          <Box p={3} bg="gray.50" borderRadius="md" _dark={{ bg: "gray.700" }}>
            <VStack align="start" gap={1}>
              <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }}>
                Condition Group
              </Text>
              <Heading size="sm">{conditionGroup.name}</Heading>
            </VStack>
          </Box>

          {/* Error Alert */}
          {(isConditionsError || isCurrentConditionsError || saveError) && (
            <ErrorAlert
              isError={true}
              title="Error"
              message={conditionsError?.toString() || currentConditionsError?.toString() || saveError?.toString() || "An error occurred"}
            />
          )}

          {isLoading ? (
            <LoadingSkeleton rows={3} />
          ) : (
            <VStack gap={4} align="stretch">
              {/* Selected Conditions Display */}
              <VStack gap={3} align="stretch">
                <HStack justify="space-between">
                  <VStack align="start" gap={0}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.700" _dark={{ color: "gray.300" }}>
                      Selected Conditions ({selectedConditionIds.length})
                    </Text>
                    <Text fontSize="xs" color="gray.500" _dark={{ color: "gray.400" }}>
                      Conditions assigned to this group
                    </Text>
                  </VStack>
                  {selectedConditionIds.length > 0 && (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setSelectedConditionIds([])}
                      colorPalette="red"
                    >
                      Clear All
                    </Button>
                  )}
                </HStack>
                
                {selectedConditions.length > 0 ? (
                  <Wrap gap={2}>
                    {selectedConditions.map((condition) => (
                      <Badge
                        key={condition.id}
                        colorPalette="blue"
                        size="md"
                        variant="subtle"
                        pr={1}
                      >
                        <HStack gap={1}>
                          <Text>{condition.name}</Text>
                          <Button
                            size="xs"
                            variant="ghost"
                            p={0}
                            minH="auto"
                            h="auto"
                            onClick={() => handleRemoveCondition(condition.id)}
                            _hover={{ bg: "blue.200", _dark: { bg: "blue.700" } }}
                          >
                            <X size={12} />
                          </Button>
                        </HStack>
                      </Badge>
                    ))}
                  </Wrap>
                ) : (
                  <Box p={3} bg="gray.50" borderRadius="md" _dark={{ bg: "gray.800" }}>
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                      No conditions assigned to this group
                    </Text>
                  </Box>
                )}
              </VStack>

              <Separator />

              {/* Search and Available Conditions */}
              <VStack gap={3} align="stretch">
                <Text fontSize="sm" fontWeight="medium" color="gray.700" _dark={{ color: "gray.300" }}>
                  Add Conditions
                </Text>
                
                {/* Search Input */}
                <Box position="relative">
                  <Input
                    placeholder="Search conditions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="sm"
                    pl={10}
                  />
                  <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" pointerEvents="none">
                    <Search size={16} color="gray" />
                  </Box>
                </Box>

                {/* Available Conditions */}
                {allConditions.length === 0 ? (
                  <Box p={4} textAlign="center" color="gray.500">
                    <Text>No conditions available</Text>
                  </Box>
                ) : availableConditions.length === 0 ? (
                  <Box p={3} bg="gray.50" borderRadius="md" _dark={{ bg: "gray.800" }}>
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                      {searchTerm ? 'No conditions match your search' : 'All conditions are already selected'}
                    </Text>
                  </Box>
                ) : (
                  <VStack gap={2} align="stretch" maxH="300px" overflowY="auto">
                    {availableConditions.map((condition) => (
                      <Box
                        key={condition.id}
                        p={3}
                        borderWidth={1}
                        borderRadius="md"
                        borderColor="gray.200"
                        bg="white"
                        _dark={{
                          borderColor: "gray.600",
                          bg: "gray.700"
                        }}
                        cursor="pointer"
                        onClick={() => handleAddCondition(condition.id)}
                        _hover={{
                          borderColor: "brand.300",
                          bg: "brand.50",
                          _dark: { 
                            borderColor: "brand.400",
                            bg: "brand.900"
                          }
                        }}
                      >
                        <VStack align="start" gap={1}>
                          <Text fontWeight="medium">{condition.name}</Text>
                          <HStack>
                             <Badge colorPalette="purple" size="sm">
                               {condition.speciality.title}
                             </Badge>
                            <Text fontSize="xs" color="gray.500">
                              ID: {condition.id}
                            </Text>
                          </HStack>
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </VStack>
            </VStack>
          )}
        </VStack>
      </DrawerBody>

      <DrawerFooter>
        <HStack gap={3} width="full">
          <Button
            variant="ghost"
            onClick={onClose}
            flex={1}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            colorPalette="brand"
            onClick={handleSave}
            loading={isSaving}
            loadingText="Saving..."
            flex={1}
            disabled={!canSave || isLoading}
          >
            <HStack>
              <Save size={16} />
              <Text>Save Changes</Text>
            </HStack>
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

// Wrap with HOC for hash-based rendering (conditions action)
export default withHashDrawer(ConditionGroupConditionsModal, { 
  action: 'conditions' 
}); 